import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api, { cardsAPI, dataAPI } from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import {
  Card, PillButton, Chip, SegmentedControl, IconCircle, EmptyState, Modal, Input, FeatureBanner,
} from '../ui';
import LanguageSelector from '../components/LanguageSelector';
import ExtensionUpdate from '../components/ExtensionUpdate';
import ImportExport from '../components/ImportExport';
import SmartCardBackup from '../components/SmartCardBackup';
import GlobalAudit from '../components/GlobalAudit';
import { getActionLabel, getActionDetail } from '../utils/auditLabels';
import { ReactComponent as OfficeIcon } from '../assets/icons/office-building.svg';
import { ReactComponent as SunIcon } from '../assets/icons/sun.svg';
import { ReactComponent as MoonIcon } from '../assets/icons/moon.svg';
import { ReactComponent as MonitorIcon } from '../assets/icons/monitor.svg';
import { ReactComponent as CreditCardIcon } from '../assets/icons/credit-card.svg';
import { ReactComponent as NfcIcon } from '../assets/icons/nfc.svg';
import { ReactComponent as MailIcon } from '../assets/icons/mail.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as SmartphoneIcon } from '../assets/icons/smartphone.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as CircleCheckIcon } from '../assets/icons/circle-check.svg';
import { ReactComponent as BellIcon } from '../assets/icons/bell.svg';
import { ReactComponent as ActivityIcon } from '../assets/icons/activity.svg';
import { ReactComponent as PuzzleIcon } from '../assets/icons/puzzle.svg';
import { ReactComponent as LogOutIcon } from '../assets/icons/log-out.svg';
import { ReactComponent as MonitorSessionIcon } from '../assets/icons/monitor.svg';
import { ReactComponent as UserIcon } from '../assets/icons/user.svg';
import { ReactComponent as SlidersIcon } from '../assets/icons/sliders-vertical.svg';
import { ReactComponent as UploadIcon } from '../assets/icons/upload.svg';
import { ReactComponent as ShieldCheckIcon } from '../assets/icons/shield-check.svg';
import { ReactComponent as FingerprintIcon } from '../assets/icons/fingerprint.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import './Account.css';

// Único punto de descarga público: la sección de descargas de la landing
// (extensión y apps móviles viven ahí, no hay ruta separada en el panel).
const DOWNLOADS_URL = 'https://securegatecard.nordicbyte.co.uk/#descargas';

const IDENTITY_ICONS = {
  card: CreditCardIcon,
  nfc: NfcIcon,
  email: MailIcon,
  master_password: KeyIcon,
  device: SmartphoneIcon,
};

// Resumen legible del user-agent, sin dependencia nueva: solo lo que hace
// falta distinguir en una lista de sesiones.
function summarizeUserAgent(ua) {
  if (!ua) return '—';
  let browser = 'Navegador';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  let os = '';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return os ? `${browser} · ${os}` : browser;
}

function relativeTime(dateStr, lang, neverLabel) {
  if (!dateStr) return neverLabel;
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.round(diffMs / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const units = [
    ['year', 31536000], ['month', 2592000], ['week', 604800],
    ['day', 86400], ['hour', 3600], ['minute', 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diffSec) >= secs) return rtf.format(-Math.round(diffSec / secs), unit);
  }
  return rtf.format(-diffSec, 'second');
}

function Account({ onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const rfidId = localStorage.getItem('rfidId');
  const authToken = localStorage.getItem('authToken');

  const [cardName, setCardName] = useState('');
  const [org, setOrg] = useState(null); // { organization, role } | null

  const [identities, setIdentities] = useState([]);
  const [loadingIdentities, setLoadingIdentities] = useState(true);

  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [pushTokens, setPushTokens] = useState([]);
  const [activity, setActivity] = useState([]);

  const [showExtension, setShowExtension] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showBackup, setShowBackup] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [passkeyCount, setPasskeyCount] = useState(0);

  // --- Carga inicial ---
  useEffect(() => {
    if (!rfidId) return;
    cardsAPI.get(rfidId).then((res) => setCardName(res.data?.name || '')).catch(() => {});
    api.get('/org/mine').then((res) => {
      const orgs = res.data?.organizations || [];
      setOrg(orgs[0] || null);
    }).catch(() => setOrg(null));

    reloadIdentities();
    reloadSessions();
    reloadDevices();
    api.get('/push/tokens').then((res) => setPushTokens(res.data?.tokens || [])).catch(() => setPushTokens([]));
    api.get(`/audit/${rfidId}`, { params: { limit: 20 } }).then((res) => setActivity(res.data?.logs || [])).catch(() => setActivity([]));
    dataAPI.stats(rfidId).then((res) => setPasskeyCount(res.data?.byType?.passkey || 0)).catch(() => setPasskeyCount(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfidId]);

  const reloadIdentities = useCallback(() => {
    setLoadingIdentities(true);
    api.get('/account/identities')
      .then((res) => setIdentities(res.data?.identities || []))
      .catch(() => setIdentities([]))
      .finally(() => setLoadingIdentities(false));
  }, []);

  const reloadSessions = useCallback(() => {
    if (!rfidId) return;
    api.get(`/auth/sessions/${rfidId}`).then((res) => setSessions(res.data?.sessions || [])).catch(() => setSessions([]));
  }, [rfidId]);

  const reloadDevices = useCallback(() => {
    api.get('/devices').then((res) => setDevices(res.data?.devices || [])).catch(() => setDevices([]));
  }, []);

  // --- Perfil ---
  const initial = (cardName || '?').trim().charAt(0).toUpperCase() || '?';
  const themeOptions = [
    { value: 'system', label: t('account.profile.themeSystem'), icon: MonitorIcon },
    { value: 'dark', label: t('account.profile.themeDark'), icon: MoonIcon },
    { value: 'light', label: t('account.profile.themeLight'), icon: SunIcon },
  ];

  // --- Metodos de acceso: vinculacion + fusion ---
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkMethod, setLinkMethod] = useState('email');
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkCardId, setLinkCardId] = useState('');
  const [linkMasterPassword, setLinkMasterPassword] = useState('');
  const [linkMasterConfirm, setLinkMasterConfirm] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  // Segundo paso: fusion pendiente de prueba
  const [pendingMerge, setPendingMerge] = useState(null); // { type, identifier, secret }
  const [mergeProofMethod, setMergeProofMethod] = useState('master');
  const [mergeMasterPassword, setMergeMasterPassword] = useState('');
  const [mergeEmail, setMergeEmail] = useState('');
  const [mergePassword, setMergePassword] = useState('');

  // Resultado de una fusion recien hecha: para ofrecer "importar ahora"
  const [mergeResult, setMergeResult] = useState(null); // { otherRfidId, otherItems }

  const linkMethodOptions = useMemo(() => ([
    { value: 'email', label: t('account.methods.linkEmail'), icon: MailIcon },
    { value: 'card', label: t('account.methods.linkCard'), icon: CreditCardIcon },
    { value: 'master', label: t('account.methods.linkMaster'), icon: KeyIcon },
  ]), [t]);

  const resetLinkForm = useCallback(() => {
    setLinkMethod('email');
    setLinkEmail(''); setLinkPassword('');
    setLinkCardId('');
    setLinkMasterPassword(''); setLinkMasterConfirm('');
    setLinkError('');
    setPendingMerge(null);
    setMergeProofMethod('master');
    setMergeMasterPassword(''); setMergeEmail(''); setMergePassword('');
  }, []);

  const openLinkModal = useCallback(() => { resetLinkForm(); setLinkModalOpen(true); }, [resetLinkForm]);
  const closeLinkModal = useCallback(() => { setLinkModalOpen(false); resetLinkForm(); }, [resetLinkForm]);

  const submitLink = useCallback(async (payload) => {
    setLinkError(''); setLinkLoading(true);
    try {
      const res = await api.post('/account/identities/link', payload);
      if (res.data?.merged) {
        setMergeResult({ otherRfidId: res.data.otherRfidId, otherItems: res.data.otherItems || 0 });
        setPendingMerge(null);
        setLinkModalOpen(false);
      } else {
        setLinkModalOpen(false);
        resetLinkForm();
      }
      reloadIdentities();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresProof) {
        setPendingMerge({ type: payload.type, identifier: payload.identifier, secret: payload.secret });
        setLinkError('');
      } else {
        setLinkError(data?.error || t('account.methods.errorGeneric'));
      }
    } finally {
      setLinkLoading(false);
    }
  }, [reloadIdentities, resetLinkForm, t]);

  const handleLinkSubmit = useCallback((e) => {
    e.preventDefault();
    if (linkMethod === 'email') {
      if (!linkEmail || !linkPassword) { setLinkError(t('account.methods.errorRequired')); return; }
      if (linkPassword.length < 8) { setLinkError(t('account.methods.errorPasswordShort')); return; }
      submitLink({ type: 'email', identifier: linkEmail.trim().toLowerCase(), secret: linkPassword });
    } else if (linkMethod === 'card') {
      if (!linkCardId) { setLinkError(t('account.methods.errorRequired')); return; }
      submitLink({ type: 'card', identifier: linkCardId.trim() });
    } else if (linkMethod === 'master') {
      if (!linkMasterPassword) { setLinkError(t('account.methods.errorRequired')); return; }
      if (linkMasterPassword.length < 6) { setLinkError(t('account.methods.errorPasswordShort')); return; }
      if (linkMasterPassword !== linkMasterConfirm) { setLinkError(t('account.methods.errorPasswordMismatch')); return; }
      submitLink({ type: 'master_password', secret: linkMasterPassword });
    }
  }, [linkMethod, linkEmail, linkPassword, linkCardId, linkMasterPassword, linkMasterConfirm, submitLink, t]);

  const handleMergeSubmit = useCallback((e) => {
    e.preventDefault();
    if (!pendingMerge) return;
    const payload = { type: pendingMerge.type, identifier: pendingMerge.identifier, secret: pendingMerge.secret };
    if (pendingMerge.type === 'email') {
      // El propio email es la prueba: la contraseña ACTUAL de esa cuenta.
      if (!mergePassword) { setLinkError(t('account.methods.errorRequired')); return; }
      payload.proof = { email: pendingMerge.identifier, password: mergePassword };
    } else if (mergeProofMethod === 'master') {
      if (!mergeMasterPassword) { setLinkError(t('account.methods.errorRequired')); return; }
      payload.proof = { masterPassword: mergeMasterPassword };
    } else {
      if (!mergeEmail || !mergePassword) { setLinkError(t('account.methods.errorRequired')); return; }
      payload.proof = { email: mergeEmail.trim().toLowerCase(), password: mergePassword };
    }
    submitLink(payload);
  }, [pendingMerge, mergeProofMethod, mergeMasterPassword, mergeEmail, mergePassword, submitLink, t]);

  const handleRemoveIdentity = useCallback(async (identity) => {
    if (!window.confirm(t('account.methods.removeConfirm', { id: identity.identifier }))) return;
    try {
      await api.delete(`/account/identities/${identity.id}`);
      reloadIdentities();
    } catch (err) {
      window.alert(err.response?.data?.error || t('account.methods.removeError'));
    }
  }, [reloadIdentities, t]);

  // --- Sesiones ---
  const currentTokenPrefix = authToken ? `${authToken.substring(0, 8)}...` : null;

  const handleCloseAllSessions = useCallback(async () => {
    if (!window.confirm(t('account.sessions.closeAllConfirm'))) return;
    try {
      const res = await api.delete(`/auth/sessions/${rfidId}`);
      window.alert(t('account.sessions.closeAllDone', { count: res.data?.closedSessions ?? '' }));
      onLogout?.();
    } catch {
      window.alert(t('account.sessions.closeAllError'));
    }
  }, [rfidId, onLogout, t]);

  // --- Dispositivos ---
  const [showLinkDevice, setShowLinkDevice] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [newDeviceType, setNewDeviceType] = useState('custom');

  const deviceTypeOptions = [
    { value: 'smart_card', label: t('account.sessions.deviceType.smart_card') },
    { value: 'yubikey', label: t('account.sessions.deviceType.yubikey') },
    { value: 'custom', label: t('account.sessions.deviceType.custom') },
  ];

  const handleUnlinkDevice = useCallback(async (device) => {
    if (!window.confirm(t('account.sessions.unlinkConfirm', { name: device.deviceName }))) return;
    try {
      await api.delete(`/devices/${device.id}`);
      reloadDevices();
    } catch {
      window.alert(t('account.sessions.unlinkError'));
    }
  }, [reloadDevices, t]);

  const handleLinkDevice = useCallback(async (e) => {
    e.preventDefault();
    if (!newDeviceId.trim()) return;
    try {
      await api.post('/devices/link', { deviceId: newDeviceId.trim(), deviceType: newDeviceType });
      setNewDeviceId('');
      setShowLinkDevice(false);
      reloadDevices();
    } catch (err) {
      window.alert(err.response?.data?.error || t('account.sessions.linkByIdError'));
    }
  }, [newDeviceId, newDeviceType, reloadDevices, t]);

  // --- Notificaciones ---
  const handleTestPush = useCallback(async () => {
    try {
      await api.post('/push/test');
      window.alert(t('account.notifications.testSent'));
    } catch {
      window.alert(t('account.notifications.testError'));
    }
  }, [t]);

  // --- Zona peligrosa ---
  const handleLogout = useCallback(() => { onLogout?.(); }, [onLogout]);

  return (
    <div className="sgc-account">
      {/* 1. Perfil */}
      <Card>
        <div className="sgc-account__profile">
          <span className="sgc-account__avatar">{initial}</span>
          <div className="sgc-account__profile-info">
            <p className="sgc-account__name">{cardName || '—'}</p>
            <p className="sgc-account__plan">
              {org ? (
                <>
                  <OfficeIcon width={14} height={14} aria-hidden="true" />
                  <a href="/admin">
                    {t('account.profile.planOrgRole', { org: org.organization.name, role: org.role.name })}
                  </a>
                </>
              ) : t('account.profile.planPersonal')}
            </p>
          </div>
        </div>

        <div className="sgc-account__row">
          <span className="sgc-account__row-label">{t('account.profile.theme')}</span>
          <SegmentedControl options={themeOptions} value={theme} onChange={setTheme} />
        </div>

        <div className="sgc-account__row">
          <span className="sgc-account__row-label">{t('account.profile.language')}</span>
          <LanguageSelector />
        </div>
      </Card>

      {/* 1.5 Seguridad: PIN, contraseña maestra, dispositivos de confianza */}
      <Card>
        <FeatureBanner
          icon={LockIcon}
          title={t('account.security.title')}
          subtitle={t('account.security.openSecurity')}
          onClick={() => navigate('/account/security')}
        />
      </Card>

      {/* 2. Metodos de acceso */}
      <Card>
        <h2 className="sgc-account__section-title">{t('account.methods.title')}</h2>
        {loadingIdentities ? (
          <p>{t('common.loading')}</p>
        ) : identities.length === 0 ? (
          <EmptyState icon={UserIcon} title={t('account.sessions.noDevices')} />
        ) : (
          <div className="sgc-account__pills">
            {identities.map((identity) => {
              const Icon = IDENTITY_ICONS[identity.type] || UserIcon;
              return (
                <div key={identity.id} className="sgc-account__pill">
                  <IconCircle icon={Icon} tone="surface" size={36} />
                  <div className="sgc-account__pill-info">
                    <p className="sgc-account__pill-id">
                      {t(`account.methods.type.${identity.type}`)}
                      <Chip label={identity.identifier} />
                      {identity.verified && (
                        <span className="sgc-account__pill-verified" title={t('account.methods.verified')}>
                          <CircleCheckIcon width={14} height={14} aria-hidden="true" />
                        </span>
                      )}
                    </p>
                    <p className="sgc-account__pill-meta">
                      {t('account.methods.lastUsed', { date: relativeTime(identity.lastUsed, i18n.language, t('account.methods.never')) })}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="sgc-account__pill-remove"
                    onClick={() => handleRemoveIdentity(identity)}
                    title={t('account.methods.remove')}
                  >
                    <TrashIcon width={16} height={16} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <PillButton icon={PlusIcon} variant="secondary" className="sgc-account__add-method" onClick={openLinkModal}>
          {t('account.methods.add')}
        </PillButton>

        {mergeResult && (
          <div className="sgc-account__merge-import" style={{ marginTop: 'var(--space-4)' }}>
            <span>{t('account.methods.mergeWarning', { count: mergeResult.otherItems })}</span>
            <PillButton
              variant="secondary"
              size="sm"
              onClick={() => window.alert(t('account.methods.importTodo'))}
            >
              {t('account.methods.importNow')}
            </PillButton>
          </div>
        )}
      </Card>

      {/* 3. Sesiones y dispositivos */}
      <Card>
        <h2 className="sgc-account__section-title">{t('account.sessions.title')}</h2>
        {sessions.length === 0 ? (
          <EmptyState icon={MonitorSessionIcon} title={t('account.sessions.noSessions')} />
        ) : (
          <div className="sgc-account__list">
            {sessions.map((s) => (
              <div key={s.token} className="sgc-account__list-item">
                <IconCircle icon={MonitorSessionIcon} tone="surface" size={36} />
                <div className="sgc-account__list-info">
                  <p className="sgc-account__list-title">
                    {summarizeUserAgent(s.userAgent)}
                    {s.token === currentTokenPrefix && <Chip label={t('account.sessions.thisSession')} selected />}
                  </p>
                  <p className="sgc-account__list-meta">
                    {s.ipAddress || '—'} · {relativeTime(s.lastActivity, i18n.language, t('account.methods.never'))}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <PillButton variant="danger" className="sgc-account__add-method" onClick={handleCloseAllSessions}>
          {t('account.sessions.closeAll')}
        </PillButton>

        <h3 className="sgc-account__section-title" style={{ marginTop: 'var(--space-6)' }}>
          {t('account.sessions.devicesTitle')}
        </h3>
        {devices.length === 0 ? (
          <EmptyState icon={SmartphoneIcon} title={t('account.sessions.noDevices')} />
        ) : (
          <div className="sgc-account__list">
            {devices.map((d) => (
              <div key={d.id} className="sgc-account__list-item">
                <IconCircle icon={SmartphoneIcon} tone="surface" size={36} />
                <div className="sgc-account__list-info">
                  <p className="sgc-account__list-title">{d.deviceName}</p>
                  <p className="sgc-account__list-meta">
                    {d.deviceType} · {t('account.methods.lastUsed', { date: relativeTime(d.lastUsed, i18n.language, t('account.methods.never')) })}
                  </p>
                </div>
                <button type="button" className="sgc-account__list-action" onClick={() => handleUnlinkDevice(d)} title={t('account.sessions.unlink')}>
                  <TrashIcon width={16} height={16} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}

        {!showLinkDevice ? (
          <PillButton icon={PlusIcon} variant="secondary" className="sgc-account__add-method" onClick={() => setShowLinkDevice(true)}>
            {t('account.sessions.linkById')}
          </PillButton>
        ) : (
          <form className="sgc-account__link-by-id" onSubmit={handleLinkDevice}>
            <Input
              label={t('account.sessions.linkByIdLabel')}
              value={newDeviceId}
              onChange={(e) => setNewDeviceId(e.target.value)}
              autoFocus
            />
            <SegmentedControl options={deviceTypeOptions} value={newDeviceType} onChange={setNewDeviceType} />
            <PillButton type="submit" variant="primary">{t('account.sessions.linkByIdSubmit')}</PillButton>
          </form>
        )}
      </Card>

      {/* 4. Notificaciones */}
      <Card>
        <h2 className="sgc-account__section-title">
          <BellIcon width={16} height={16} aria-hidden="true" /> {t('account.notifications.title')}
        </h2>
        {pushTokens.length === 0 ? (
          <EmptyState icon={BellIcon} title={t('account.notifications.noTokens')} />
        ) : (
          <div className="sgc-account__list">
            {pushTokens.map((tok) => (
              <div key={tok.id} className="sgc-account__list-item">
                <IconCircle icon={SmartphoneIcon} tone="surface" size={36} />
                <div className="sgc-account__list-info">
                  <p className="sgc-account__list-title">{tok.deviceName || tok.platform}</p>
                  <p className="sgc-account__list-meta">{tok.platform} · {new Date(tok.createdAt).toLocaleDateString(i18n.language)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <PillButton variant="secondary" className="sgc-account__add-method" onClick={handleTestPush}>
          {t('account.notifications.sendTest')}
        </PillButton>
      </Card>

      {/* 5. Actividad reciente */}
      <Card>
        <h2 className="sgc-account__section-title">
          <ActivityIcon width={16} height={16} aria-hidden="true" /> {t('account.activity.title')}
        </h2>
        {activity.length === 0 ? (
          <EmptyState icon={ActivityIcon} title={t('account.activity.empty')} />
        ) : (
          activity.map((log) => {
            const detail = getActionDetail(t, log);
            return (
              <div key={log.id} className="sgc-account__activity-item">
                <span className={`sgc-account__activity-dot sgc-account__activity-dot--${log.metadata?.success === false ? 'fail' : 'success'}`} />
                <div className="sgc-account__activity-body">
                  <span className="sgc-account__activity-action">{getActionLabel(t, log.action)}</span>
                  {detail && <span className="sgc-account__activity-detail">{detail}</span>}
                </div>
                <span className="sgc-account__activity-time">{relativeTime(log.timestamp, i18n.language, '')}</span>
              </div>
            );
          })
        )}
      </Card>

      {/* 6. Extension */}
      <Card>
        <h2 className="sgc-account__section-title">
          <PuzzleIcon width={16} height={16} aria-hidden="true" /> {t('account.extension.title')}
        </h2>
        <PillButton icon={PuzzleIcon} variant="secondary" onClick={() => setShowExtension(true)}>
          {t('account.extension.open')}
        </PillButton>
      </Card>
      {showExtension && <ExtensionUpdate onClose={() => setShowExtension(false)} />}

      {/* 6a. Passkeys */}
      <Card>
        <h2 className="sgc-account__section-title">
          <FingerprintIcon width={16} height={16} aria-hidden="true" /> {t('account.passkeys.title')}
        </h2>
        <p className="sgc-account__list-meta">{t('account.passkeys.explain')}</p>
        <p className="sgc-account__list-meta">{t('account.passkeys.count', { count: passkeyCount })}</p>
        <div className="sgc-account__tools">
          <PillButton icon={PuzzleIcon} variant="secondary" onClick={() => window.open(DOWNLOADS_URL, '_blank', 'noopener')}>
            {t('account.passkeys.getExtension')}
          </PillButton>
          <PillButton icon={SmartphoneIcon} variant="secondary" onClick={() => window.open(DOWNLOADS_URL, '_blank', 'noopener')}>
            {t('account.passkeys.getApp')}
          </PillButton>
        </div>
      </Card>

      {/* 6b. Herramientas: lo viejo que no se migró al diseño nuevo, cada uno
          envuelto en el Modal del kit para que no mezcle su propio estilo con
          el del shell (ImportExport ya trae su propio overlay, así que se
          monta directo, igual que ExtensionUpdate arriba). */}
      <Card>
        <h2 className="sgc-account__section-title">
          <SlidersIcon width={16} height={16} aria-hidden="true" /> {t('account.tools.title')}
        </h2>
        <div className="sgc-account__tools">
          <PillButton icon={UploadIcon} variant="secondary" onClick={() => setShowImportExport(true)}>
            {t('account.tools.importExport')}
          </PillButton>
          <PillButton icon={NfcIcon} variant="secondary" onClick={() => setShowBackup(true)}>
            {t('account.tools.cardBackup')}
          </PillButton>
          <PillButton icon={ShieldCheckIcon} variant="secondary" onClick={() => setShowAudit(true)}>
            {t('account.tools.globalAudit')}
          </PillButton>
        </div>
      </Card>
      {showImportExport && (
        <ImportExport onClose={() => setShowImportExport(false)} onImportComplete={() => setShowImportExport(false)} />
      )}
      <Modal open={showBackup} onClose={() => setShowBackup(false)} title={t('account.tools.cardBackup')}>
        <SmartCardBackup onClose={() => setShowBackup(false)} />
      </Modal>
      <Modal open={showAudit} onClose={() => setShowAudit(false)} title={t('account.tools.globalAudit')} className="sgc-account__audit-modal">
        <GlobalAudit />
      </Modal>

      {/* 7. Zona peligrosa */}
      <Card className="sgc-account__danger">
        <h2 className="sgc-account__section-title">{t('account.danger.title')}</h2>
        <PillButton icon={LogOutIcon} variant="danger" onClick={handleLogout}>
          {t('account.danger.logout')}
        </PillButton>
      </Card>

      {/* Modal: vincular metodo */}
      <Modal open={linkModalOpen} onClose={closeLinkModal} title={t('account.methods.linkTitle')}>
        {!pendingMerge ? (
          <form className="sgc-account__form" onSubmit={handleLinkSubmit}>
            <SegmentedControl options={linkMethodOptions} value={linkMethod} onChange={setLinkMethod} />
            {linkError && <p className="sgc-account__form-error">{linkError}</p>}

            {linkMethod === 'email' && (
              <>
                <Input icon={MailIcon} label={t('account.methods.emailLabel')} type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} />
                <Input icon={KeyIcon} label={t('account.methods.passwordLabel')} type="password" value={linkPassword} onChange={(e) => setLinkPassword(e.target.value)} />
              </>
            )}

            {linkMethod === 'card' && (
              <div className="sgc-account__scan-prompt">
                <CreditCardIcon width={32} height={32} aria-hidden="true" />
                <p>{t('account.methods.cardPrompt')}</p>
                <Input icon={CreditCardIcon} label={t('account.methods.cardIdLabel')} value={linkCardId} onChange={(e) => setLinkCardId(e.target.value)} autoFocus />
              </div>
            )}

            {linkMethod === 'master' && (
              <>
                <Input icon={KeyIcon} label={t('account.methods.masterPasswordLabel')} type="password" value={linkMasterPassword} onChange={(e) => setLinkMasterPassword(e.target.value)} />
                <Input icon={KeyIcon} label={t('account.methods.confirmPasswordLabel')} type="password" value={linkMasterConfirm} onChange={(e) => setLinkMasterConfirm(e.target.value)} />
              </>
            )}

            <PillButton type="submit" variant="primary" disabled={linkLoading}>
              {t('account.methods.linkSubmit')}
            </PillButton>
          </form>
        ) : (
          <form className="sgc-account__form" onSubmit={handleMergeSubmit}>
            <p className="sgc-account__merge-warning">
              {pendingMerge.type === 'email' ? t('account.methods.mergeExplainEmail') : t('account.methods.mergeExplainPhysical')}
            </p>
            {linkError && <p className="sgc-account__form-error">{linkError}</p>}

            {pendingMerge.type === 'email' ? (
              <Input icon={KeyIcon} label={t('account.methods.mergeProofPasswordLabel')} type="password" value={mergePassword} onChange={(e) => setMergePassword(e.target.value)} autoFocus />
            ) : (
              <>
                <SegmentedControl
                  options={[
                    { value: 'master', label: t('account.methods.mergeProofMaster'), icon: KeyIcon },
                    { value: 'email', label: t('account.methods.mergeProofEmail'), icon: MailIcon },
                  ]}
                  value={mergeProofMethod}
                  onChange={setMergeProofMethod}
                />
                {mergeProofMethod === 'master' ? (
                  <Input icon={KeyIcon} label={t('account.methods.masterPasswordLabel')} type="password" value={mergeMasterPassword} onChange={(e) => setMergeMasterPassword(e.target.value)} />
                ) : (
                  <>
                    <Input icon={MailIcon} label={t('account.methods.mergeProofEmailLabel')} type="email" value={mergeEmail} onChange={(e) => setMergeEmail(e.target.value)} />
                    <Input icon={KeyIcon} label={t('account.methods.mergeProofPasswordLabel')} type="password" value={mergePassword} onChange={(e) => setMergePassword(e.target.value)} />
                  </>
                )}
              </>
            )}

            <PillButton type="submit" variant="primary" disabled={linkLoading}>
              {t('account.methods.mergeConfirm')}
            </PillButton>
          </form>
        )}
      </Modal>
    </div>
  );
}

export default Account;
